/* ==========================================================================
   ergonix-omnimat-set-nudge.js - "take all three mats as a set" in the cart.

   The Omni MAT set is its own variant (SKU OMMATSET-B / OMMATSET-G, 46,90 EUR against a
   compare-at of 67,00 EUR = the three sizes at list price). A customer who adds the sizes one
   by one still pays list price for each. The owner chose a NUDGE over an automatic swap
   (2026-09-11): when the cart holds two or more DIFFERENT sizes of one colour and not that
   colour's set, the cart shows a small box with the set's own price and compare-at, and a
   button that swaps one of each size for the set. Nobody's price changes on its own.

   UPGRADE MODE (owner, 2026-09-16). The desk page sells the 90x40 mat as an add-on at 29 EUR
   (MAT15), and the set costs 38.99 with a desk (SET39) - so once a desk and a lone mat are in the
   cart, the box offers exactly that difference: "top up to the whole set for 9.99". The top-up is
   COMPUTED from the market's own prices and the two codes' rates, never a hardcoded 9,99 EUR,
   because CZ prices in CZK and any future price change must stay correct.

   RULES THIS FILE KEEPS
   - It never edits cart.js. It draws from window.cart.onRender (the same view every paint
     draws) and writes only through window.cart.addToCart and setQuantityByGroup, so the
     cart's intent/write chain stays the single writer (SENSITIVE-CONTRACTS #21).
   - Lines are matched by SKU, which is identical in every market. Never by title or option
     name, which Shopify translates (CLAUDE.md multi-market rule #4).
   - Every number shown is Shopify's: the set variant's price and compare_at_price, emitted by
     the layout into cartStrings.omniMatSet. Nothing is computed, so nothing can go stale.
   - A product that is not published is not in all_products, so cartStrings.omniMatSet is null
     and this file does nothing at all until the mat goes live.

   Tested offline by scripts/omnimat-set-nudge.test.mjs (decide + plan).
   ========================================================================== */
(function () {
  'use strict';

  // Warehouse SKUs (Talga): OMAT01 = 90 x 40 desk mat, MouPad01 = mouse mat, CupPad01 = 4 cup pads.
  var PART_RE = /^(OMAT|MouPad|CupPad)01([BG])$/i;
  var SIZE_OF = { omat: '1', moupad: '2', cuppad: '3' };
  var PART = { exec: function (sku) {                // [whole, size 1..3, colour], like the old regex
    var m = PART_RE.exec(sku);
    return m ? [m[0], SIZE_OF[m[1].toLowerCase()], m[2]] : null;
  } };
  var SET = /^OMMATSET-([BG])$/i;
  var SKU_OF = { '1': 'OMAT01', '2': 'MOUPAD01', '3': 'CUPPAD01' };   // size -> SKU stem

  function byColour(items) {
    var by = {};
    (items || []).forEach(function (i) {
      if (!i || (i.properties && i.properties._gift)) return;
      var sku = String(i.sku || '').trim(), m, c, g;
      if ((m = PART.exec(sku))) {
        c = m[2].toUpperCase();
        g = by[c] || (by[c] = { sizes: {}, items: [], hasSet: false });
        g.sizes[m[1]] = true;
        g.items.push(i);
      } else if ((m = SET.exec(sku))) {
        c = m[1].toUpperCase();
        (by[c] || (by[c] = { sizes: {}, items: [], hasSet: false })).hasSet = true;
      }
    });
    return by;
  }

  // The colour to offer the set for, or null: two or more DIFFERENT sizes of one colour, that
  // colour's set not already in the cart, and Shopify data for an available set.
  function decide(view, sets, opts) {
    if (!view || !Array.isArray(view.items) || !sets) return null;
    opts = opts || {};
    var by = byColour(view.items), best = null;
    Object.keys(by).forEach(function (c) {
      var g = by[c], n = Object.keys(g.sizes).length, s = sets[c];
      if (g.hasSet || !s || !s.id || s.available === false) return;
      // UPGRADE: a desk, and exactly this colour's 90x40 mat. The upgrade copy NAMES what is being
      // added - "the mouse pad and the 4 coasters" - so it is only true for this one combination.
      // `have` travels with it so the top-up is priced from the piece actually held.
      if (opts.deskQty > 0 && n === 1 && g.sizes['1']) {
        if (!best || best.mode !== 'upgrade') best = { colour: c, sizes: n, mode: 'upgrade', have: g.sizes };
        return;
      }
      // SET: with a desk in the cart, ANY single piece earns the offer - a customer holding only the
      // mouse pad used to be shown nothing at all. Its copy is about taking all three together, so
      // it is true whichever pieces they hold. Without a desk it still takes two different pieces:
      // the set carries no discount then, and swapping one piece for it is not obviously a saving.
      if (n < (opts.deskQty > 0 ? 1 : 2)) return;
      if (!best) best = { colour: c, sizes: n, mode: 'combine', have: g.sizes };
      else if (best.mode === 'combine' && n > best.sizes) best = { colour: c, sizes: n, mode: 'combine', have: g.sizes };
    });
    return best;
  }

  // What topping up costs: the set at its desk price minus the mat at its desk price. The two cuts
  // are FIXED amounts in Shopify (MAT15 -10.00, SET39 -7.91), because a percentage cannot land on
  // 29.00 from 39.00 - four decimal places of percent leave a cent behind, and the cart showed
  // 29.01. Fixed amounts are stored in the shop's own currency, so a market that checks out in
  // another currency gets no number rather than a converted guess; the copy drops the price there.
  function topUpCents(colour, have) {
    var cs = window.cartStrings || {};
    var set = (cs.omniMatSet || {})[colour];
    var parts = cs.omniMatParts;
    var cuts = cs.omniMatCuts || {};
    if (!set || !parts || typeof cuts.set !== 'number' || typeof cuts.mat !== 'number') return null;
    if (cuts.currency && window.shopCurrencyCode && cuts.currency !== window.shopCurrencyCode) return null;
    // What the customer already pays for the pieces they hold. Only the 90x40 mat carries a cut of
    // its own (MAT15); the mouse pad and the cup pads have none, so they count at list price.
    var held = 0, sizes = have || {};
    for (var size in sizes) {
      if (!sizes[size] || !SKU_OF[size]) continue;
      var part = parts[SKU_OF[size] + colour];
      if (!part || typeof part.price !== 'number') return null;   // cannot price it -> do not offer
      held += part.price - (size === '1' ? cuts.mat : 0);
    }
    if (!held) return null;
    var diff = (set.price - cuts.set) - held;
    return diff > 0 ? diff : null;
  }

  // What the switch writes besides adding one set: ONE unit fewer of each size the customer has
  // in that colour. Quantities are summed per line group, because Shopify splits one variant
  // into several lines when their discounts differ.
  function plan(items, colour, groupOf) {
    var groups = {}, order = [];
    (items || []).forEach(function (i) {
      if (!i || (i.properties && i.properties._gift)) return;
      var m = PART.exec(String(i.sku || '').trim());
      if (!m || m[2].toUpperCase() !== colour) return;
      var g = groupOf(i);
      if (!groups[g]) { groups[g] = { size: m[1], qty: 0 }; order.push(g); }
      groups[g].qty += (i.quantity || 0);
    });
    var seen = {}, steps = [];
    order.forEach(function (g) {
      var e = groups[g];
      if (seen[e.size]) return;          // one unit per SIZE, even if a size sits in two groups
      seen[e.size] = true;
      steps.push({ group: g, target: Math.max(0, e.qty - 1) });
    });
    return steps;
  }

  function money(cents) {
    return typeof window.formatMoney === 'function' ? window.formatMoney(cents) : (cents / 100).toFixed(2);
  }

  // Escapes text that goes into innerHTML. The copy is translator-supplied, so it is data.
  function esc(t) {
    return String(t == null ? '' : t)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  // Fills {price}/{was} in a translated sentence and marks the money up, so the number can carry
  // the weight without the whole line shouting. Everything but the numbers is escaped.
  function sentence(tpl, price, was) {
    var out = esc(tpl);
    if (price != null) out = out.replace('{price}', '<b class="omset__now">' + esc(price) + '</b>');
    if (was != null) out = out.replace('{was}', '<s class="omset__was">' + esc(was) + '</s>');
    return out;
  }

  function css() {
    if (document.getElementById('omnimat-set-nudge-css')) return;
    var s = document.createElement('style');
    s.id = 'omnimat-set-nudge-css';
    s.textContent =
      // The offer hangs off the mat's own cart line, so it is drawn as a continuation of that row
      // rather than as a banner: one hairline card, a caret pointing back up at the line it belongs
      // to, and no colour of its own. Everything that moves uses one curve, so the box, the button
      // and the swap read as a single gesture.
      // The offer lives in a ~370px drawer column on a 1700px screen, so a viewport breakpoint never
      // fires there and the sentence squeezed into three lines beside the button. The slot is a
      // container, and the box answers to ITS width. The viewport query below stays for browsers
      // without container queries, where the phone case is the one that matters.
      '.cart-item__nudge,.ecart-item__nudge{display:block;container-type:inline-size}' +
      '.cart-item__nudge[hidden],.ecart-item__nudge[hidden]{display:none}' +
      '.omset{--omset-ease:cubic-bezier(.32,.72,0,1);position:relative;display:flex;flex-direction:column;align-items:stretch;gap:12px;' +
        'margin:-2px 0 14px;padding:13px 14px;border:1px solid #E6E6E1;border-radius:14px;background:#FCFCFB;' +
        'font-family:Inter,sans-serif;transform-origin:top center;animation:omset-in .42s var(--omset-ease) both}' +
      // The caret. Two stacked triangles: the border colour behind, the fill one pixel lower.
      '.omset::before,.omset::after{content:"";position:absolute;left:26px;width:0;height:0;' +
        'border-left:7px solid transparent;border-right:7px solid transparent}' +
      '.omset::before{top:-8px;border-bottom:8px solid #E6E6E1}' +
      '.omset::after{top:-7px;border-bottom:8px solid #FCFCFB}' +
      '@keyframes omset-in{from{opacity:0;transform:translateY(-6px) scale(.985)}to{opacity:1;transform:none}}' +
      '.omset__head{display:flex;align-items:center;gap:12px;min-width:0}' +
      '.omset__img{flex:0 0 auto;width:54px;height:54px;border-radius:10px;background:#fff;' +
        'border:1px solid #EDEDE8;display:flex;align-items:center;justify-content:center;overflow:hidden}' +
      '.omset__img img{max-width:88%;max-height:88%;object-fit:contain;display:block}' +
      '.omset__txt{flex:1 1 auto;min-width:0}' +
      '.omset__t{margin:0 0 2px;font-weight:600;font-size:14px;line-height:1.35;color:#14161A;letter-spacing:-.01em}' +
      '.omset__b{margin:0;font-size:12.5px;line-height:1.45;color:#6B7079}' +
      '.omset__now{font-weight:600;color:#14161A}' +
      '.omset__was{color:#9AA0A8}' +
      '.omset__btn{flex:0 0 auto;width:100%;appearance:none;border:0;border-radius:999px;padding:11px 16px;background:#14161A;color:#fff;' +
        'font:600 13px Inter,sans-serif;letter-spacing:-.01em;cursor:pointer;white-space:nowrap;' +
        'transition:transform .18s var(--omset-ease),opacity .18s linear;will-change:transform}' +
      '.omset__btn:hover{transform:translateY(-1px)}' +
      '.omset__btn:active{transform:scale(.97)}' +
      '.omset__btn:disabled{opacity:.55;cursor:default;transform:none}' +
      '.omset__btn:focus-visible{outline:2px solid #14161A;outline-offset:2px}' +
      // Narrow drawers and phones: the button earns its own line rather than squeezing the sentence.
      // Wide enough and the action sits beside the copy instead of under it.
      '@container (min-width:431px){.omset{flex-direction:row;align-items:center;gap:14px}' +
        '.omset__head{flex:1 1 auto}.omset__btn{width:auto}}' +
      // The swap itself: the box collapses in place instead of vanishing between two paints.
      '.omset--leaving{animation:omset-out .3s var(--omset-ease) both;pointer-events:none}' +
      '@keyframes omset-out{to{opacity:0;transform:translateY(-4px) scale(.985)}}' +
      '@media (prefers-reduced-motion:reduce){.omset,.omset--leaving{animation:none}.omset__btn{transition:none}}';
    document.head.appendChild(s);
  }

  var busy = false;

  async function swap(colour, btn) {
    var c = window.cart, sets = (window.cartStrings || {}).omniMatSet;
    if (!c || busy || !sets || !sets[colour]) return;
    busy = true;
    if (btn) btn.disabled = true;
    try {
      var view = c._viewOf(c._lastCartSnapshot);
      var steps = plan(view && view.items, colour, function (i) { return c._lineGroupKey(i); });
      var added = await c.addToCart(sets[colour].id, 1, { openDrawer: false });
      // addToCart resolves null/undefined on refusal, network failure or a deduped add.
      // Keep the original mats unless Shopify confirms the requested set was added.
      if (!added || String(added.variant_id || added.id) !== String(sets[colour].id)) return;
      steps.forEach(function (s) { c.setQuantityByGroup(s.group, s.target); });
    } catch (e) {
      console.warn('[omni mat set] switch skipped:', e);
    } finally {
      busy = false;
      if (btn) btn.disabled = false;
      var card = btn && btn.closest ? btn.closest('.omset') : null;
      if (card && !matchMedia('(prefers-reduced-motion: reduce)').matches) {
        card.classList.add('omset--leaving');
        setTimeout(function () { render(c._viewOf(c._lastCartSnapshot)); }, 300);
      } else {
        render(c._viewOf(c._lastCartSnapshot));
      }
    }
  }

  function render(view) {
    var cs = window.cartStrings || {}, sets = cs.omniMatSet;
    var boxes = document.querySelectorAll('[data-omnimat-set-nudge]');
    if (!boxes.length) return;
    var deskQty = typeof window._qtyOf === 'function' && view
      ? window._qtyOf(view.items, 'biurko-z-regulacja-wysokosci-omnipro') : 0;
    var d = busy ? null : decide(view, sets, { deskQty: deskQty });
    var top = d && d.mode === 'upgrade' ? topUpCents(d.colour, d.have) : null;
    if (d && d.mode === 'upgrade' && top === null) d = null;   // no honest price -> no offer
    // SET39 takes a fixed amount off the set whenever a desk is in the cart, so with a desk the box
    // must quote 38,99 and not the 46,90 the set costs on its own. Fixed cuts are stored in the
    // shop's own currency, so a market checking out in another one keeps the plain price.
    var setNow = null;
    if (d && d.mode === 'combine' && sets[d.colour]) {
      var _cuts = cs.omniMatCuts || {};
      var _ok = deskQty > 0 && typeof _cuts.set === 'number' &&
        (!_cuts.currency || !window.shopCurrencyCode || _cuts.currency === window.shopCurrencyCode);
      setNow = _ok ? sets[d.colour].price - _cuts.set : sets[d.colour].price;
    }
    boxes.forEach(function (box) {
      if (!d) { box.hidden = true; box.innerHTML = ''; box.dataset.state = ''; return; }
      var state = d.mode + ':' + d.colour + ':' + d.sizes + ':' + (top || '') + ':' + (setNow || '');
      if (box.dataset.state === state && !box.hidden) return;   // no churn on every paint
      var s = sets[d.colour];
      box.dataset.state = state;
      // The photo is the set's own, from Shopify. An offer to swap three loose pieces for one
      // boxed set is hard to picture from a sentence; showing the thing being offered is the
      // difference between reading the box and wanting it.
      box.innerHTML = '<div class="omset">' +
                        '<div class="omset__head">' +
                          (s.image ? '<span class="omset__img"><img alt="" loading="lazy" decoding="async"></span>' : '') +
                          '<div class="omset__txt"><p class="omset__t"></p><p class="omset__b"></p></div>' +
                        '</div>' +
                        '<button type="button" class="omset__btn"></button>' +
                      '</div>';
      if (s.image) box.querySelector('.omset__img img').src = s.image;
      var isUp = d.mode === 'upgrade';
      box.querySelector('.omset__t').textContent =
        (isUp ? cs.omniMatUpgradeTitle : cs.omniMatSetTitle) || '';
      box.querySelector('.omset__b').innerHTML = isUp
        ? sentence(cs.omniMatUpgradeBody || '{price}', money(top), null)
        : sentence(cs.omniMatSetBody || '{price} / {was}',
                   money(setNow == null ? s.price : setNow), money(s.was));
      var btn = box.querySelector('.omset__btn');
      btn.textContent = String((isUp ? cs.omniMatUpgradeButton : cs.omniMatSetButton) || '')
        .replace('{price}', money(top));
      btn.addEventListener('click', function () { swap(d.colour, btn); });
      box.hidden = false;
    });
  }

  function init() {
    if (!(window.cartStrings || {}).omniMatSet) return;           // not published: stay inert
    if (!window.cart || typeof window.cart.onRender !== 'function') return;
    css();
    window.cart.onRender(render);
  }

  window.ErgonixOmniMatSet = { decide: decide, plan: plan, topUpCents: topUpCents };  // for the offline test
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
